/**
 * 14 Tage bestätigter Preis gegen Ziel der Automatik (Board „Preisautomatik · Übersicht“): eine
 * durchgezogene Linie für bestätigt, gestrichelt bernstein für das Ziel; darunter die Mindestnächte
 * je Tag, abweichende Ziele bernstein. Fehlt ein Wert, bricht die Linie – sie erfindet nichts; ein
 * einzelner Wert zwischen Lücken bleibt als Punkt sichtbar. Die Werte je Tag stehen als Liste für
 * Vorlesen und Tastatur bereit.
 */
import type { DynamicPricingCockpitDay } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/format';

const WIDTH = 280;
const HEIGHT = 80;

function path(values: (number | null)[], low: number, high: number): string {
  const step = WIDTH / Math.max(values.length - 1, 1);
  return values
    .map((value, index) => {
      if (value === null) return '';
      const x = (index * step).toFixed(1);
      const y = (HEIGHT - 6 - ((value - low) / Math.max(high - low, 1)) * (HEIGHT - 12)).toFixed(1);
      const joined = index > 0 && values[index - 1] !== null;
      const alone = !joined && (values[index + 1] ?? null) === null;
      return joined ? `L${x},${y}` : alone ? `M${x},${y} h0.1` : `M${x},${y}`;
    })
    .join(' ');
}

interface Props {
  days: DynamicPricingCockpitDay[];
  range: string;
  currency: string;
}

export function OverviewSparkline({ days, range, currency }: Props) {
  const confirmed = days.map((day) => day.confirmedRate);
  const desired = days.map((day) => day.desiredRate);
  const all = [...confirmed, ...desired].filter((value): value is number => value !== null);
  if (all.length === 0) {
    return (
      <p className="text-ink-soft bg-paper rounded-tile p-3 text-xs">
        Für diese 14 Tage liegen noch keine berechneten Werte vor.
      </p>
    );
  }
  const low = Math.min(...all) - 4;
  const high = Math.max(...all) + 4;
  const money = (rate: number | null) => (rate === null ? '—' : formatMoney(rate, currency));
  return (
    <div className="bg-paper rounded-tile p-3">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-20 w-full"
        aria-hidden
        preserveAspectRatio="none"
      >
        {days.map((day, index) => (
          <rect
            key={day.date}
            x={(index - 0.5) * (WIDTH / Math.max(days.length - 1, 1))}
            width={WIDTH / Math.max(days.length - 1, 1)}
            height={HEIGHT}
            fill="transparent"
          >
            <title>{dayText(day, money)}</title>
          </rect>
        ))}
        <path
          d={path(confirmed, low, high)}
          fill="none"
          stroke="var(--color-forest)"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <path
          d={path(desired, low, high)}
          fill="none"
          stroke="var(--color-amber)"
          strokeWidth={2}
          strokeDasharray="5 4"
          strokeLinecap="round"
        />
      </svg>
      <ul className="sr-only" aria-label={`Preis bestätigt und Ziel der Automatik, ${range}`}>
        {days.map((day) => (
          <li key={day.date}>{dayText(day, money)}</li>
        ))}
      </ul>
      <div
        aria-hidden
        className="grid font-mono text-[10px]"
        style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}
      >
        {days.map((day) => (
          <span
            key={day.date}
            className={cn(
              'text-center',
              day.desiredMinimumStay !== null && day.desiredMinimumStay !== day.confirmedMinimumStay
                ? 'text-amber-ink font-bold'
                : 'text-ink-soft',
            )}
          >
            {day.desiredMinimumStay ?? day.confirmedMinimumStay ?? '—'}
          </span>
        ))}
      </div>
      <span className="text-ink-soft block pt-0.5 text-[10px]">Mindestnächte je Tag · {range}</span>
    </div>
  );
}

function dayText(day: DynamicPricingCockpitDay, money: (rate: number | null) => string): string {
  const [, month, date] = day.date.split('-');
  return `${date}.${month}.: bestätigt ${money(day.confirmedRate)} · Ziel ${money(day.desiredRate)} · Mindestnächte ${day.confirmedMinimumStay ?? '—'} → ${day.desiredMinimumStay ?? '—'}`;
}
